import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fmtEUR, mediaUrl } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import { User, Phone, MapPin, Mail, Building2, LogOut, Package, Clock, CheckCircle2, XCircle, Truck, ShoppingBag, Edit3, Save, QrCode, Receipt, Gift, TrendingUp } from "lucide-react";

const STATUS_STYLE = {
  "E re": { color: "bg-orange-500/15 text-orange-300 border-orange-500/40", icon: Clock },
  "Në pritje": { color: "bg-orange-500/15 text-orange-300 border-orange-500/40", icon: Clock },
  "Në përpunim": { color: "bg-blue-500/15 text-blue-300 border-blue-500/40", icon: Package },
  "Duke u përgatitur": { color: "bg-purple-500/15 text-purple-300 border-purple-500/40", icon: Package },
  "Dërguar": { color: "bg-cyan-500/15 text-cyan-300 border-cyan-500/40", icon: Truck },
  "Përfunduar": { color: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", icon: CheckCircle2 },
  "Anuluar": { color: "bg-red-500/15 text-red-300 border-red-500/40", icon: XCircle },
};

export default function Profile() {
  const { user, logout, updateProfile } = useApp();
  const nav = useNavigate();
  const [tab, setTab] = useState("card");
  const [orders, setOrders] = useState([]);
  const [posInvoices, setPosInvoices] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", address: "", city: "" });
  const [enabledCountries, setEnabledCountries] = useState(["Kosovë","Shqipëri","Maqedoni e Veriut","Mal i Zi"]);

  useEffect(() => { if (!user) nav("/login", { replace: true }); }, [user, nav]);

  useEffect(() => {
    api.get("/settings").then(r => {
      if (r.data.shipping_fees && Object.keys(r.data.shipping_fees).length > 0) {
        const all = ["Kosovë","Shqipëri","Maqedoni e Veriut","Mal i Zi"];
        const enabled = all.filter(c => Object.prototype.hasOwnProperty.call(r.data.shipping_fees, c));
        if (enabled.length > 0) setEnabledCountries(enabled);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "", phone: user.phone || "",
      address: user.address || "", city: user.city || "",
      company_name: user.company_name || "",
      street: user.street || "", house_number: user.house_number || "",
      postal_code: user.postal_code || "", country: user.country || "Kosovë",
      preferred_contact: user.preferred_contact || "Telefon",
    });
    api.get("/orders/mine").then(r => { setOrders(r.data); }).catch(() => {});
    api.get("/loyalty/me").then(r => setLoyalty(r.data)).catch(() => {});
    api.get("/loyalty/me/invoices").then(r => { setPosInvoices(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [user]);

  if (!user) return null;

  const active = orders.filter(o => !["Përfunduar", "Anuluar"].includes(o.status));
  const past = orders.filter(o => ["Përfunduar", "Anuluar"].includes(o.status));

  const saveProfile = async () => {
    try { await updateProfile(form); toast.success("Të dhënat u ruajtën"); setEditing(false); }
    catch { toast.error("Gabim gjatë ruajtjes"); }
  };

  const OrderCard = ({ o }) => {
    const s = STATUS_STYLE[o.status] || STATUS_STYLE["E re"];
    const Icon = s.icon;
    return (
      <div className="hg-card p-4" data-testid={`order-card-${o.id}`}>
        <div className="flex items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-xs text-neutral-400 uppercase tracking-wider">Porosia</div>
            <div className="font-display text-lg break-all">#{o.order_number}</div>
            <div className="text-[11px] text-neutral-500 mt-0.5">{new Date(o.created_at).toLocaleString("sq-AL")}</div>
          </div>
          <div className={`text-xs uppercase tracking-wider font-bold px-3 py-1.5 border rounded ${s.color} flex items-center gap-1.5`}>
            <Icon size={12} /> {o.status}
          </div>
        </div>
        <div className="space-y-1.5 mb-3">
          {(o.items || []).slice(0, 3).map((it, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <div className="w-10 h-10 bg-white overflow-hidden flex-shrink-0 flex items-center justify-center">
                {it.image ? <img src={mediaUrl(it.image)} alt="" className="w-full h-full object-contain" /> : <Package size={16} className="text-neutral-500" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="truncate">{it.name}</div>
                <div className="text-neutral-500">{it.quantity} × {fmtEUR(it.unit_price)}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-[#1F2B44]">
          <div className="text-xs text-neutral-400">Totali</div>
          <div className="font-display text-xl text-[#FFD600]">{fmtEUR(o.total)}</div>
        </div>
      </div>
    );
  };

  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const qrImgUrl = loyalty?.qr_token ? `${apiBase}/api/admin/qr?data=${encodeURIComponent(loyalty.qr_token)}&size=10` : null;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="hg-card p-4 mb-4 flex items-center gap-3">
        <div className="w-14 h-14 rounded-full bg-[#FFD600] text-black flex items-center justify-center font-display text-2xl">
          {user.name?.charAt(0).toUpperCase() || "U"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl truncate" data-testid="profile-name">{user.name}</div>
          <div className="text-xs text-neutral-400 truncate">{user.email}</div>
          {loyalty?.client_number && <div className="text-[10px] text-[#FFD600] font-mono mt-0.5">{loyalty.client_number}</div>}
        </div>
        <button onClick={() => { logout(); nav("/"); }} data-testid="profile-logout"
          className="p-2.5 border border-[#1F2B44] hover:border-red-500 hover:text-red-400 text-neutral-400 transition-colors">
          <LogOut size={18} />
        </button>
      </div>

      <div className="grid grid-cols-4 gap-1 mb-4 p-1 bg-[#131E30] border border-[#1F2B44]">
        <button onClick={() => setTab("card")} data-testid="tab-card"
          className={`py-2.5 text-[10px] sm:text-xs uppercase tracking-widest font-bold ${tab === "card" ? "bg-[#FFD600] text-black" : "text-neutral-400"}`}>
          <QrCode size={12} className="inline mr-1 -mt-0.5" /> Karta
        </button>
        <button onClick={() => setTab("orders")} data-testid="tab-orders"
          className={`py-2.5 text-[10px] sm:text-xs uppercase tracking-widest font-bold ${tab === "orders" ? "bg-[#FFD600] text-black" : "text-neutral-400"}`}>
          <ShoppingBag size={12} className="inline mr-1 -mt-0.5" /> Porositë
        </button>
        <button onClick={() => setTab("invoices")} data-testid="tab-invoices"
          className={`py-2.5 text-[10px] sm:text-xs uppercase tracking-widest font-bold ${tab === "invoices" ? "bg-[#FFD600] text-black" : "text-neutral-400"}`}>
          <Receipt size={12} className="inline mr-1 -mt-0.5" /> Blerjet
        </button>
        <button onClick={() => setTab("profile")} data-testid="tab-profile"
          className={`py-2.5 text-[10px] sm:text-xs uppercase tracking-widest font-bold ${tab === "profile" ? "bg-[#FFD600] text-black" : "text-neutral-400"}`}>
          <User size={12} className="inline mr-1 -mt-0.5" /> Profili
        </button>
      </div>

      {tab === "card" && (
        <div className="space-y-4">
          <div className="hg-card p-6 bg-gradient-to-br from-[#131E30] to-[#0B1220] border-2 border-[#FFD600]" data-testid="loyalty-card">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-400">Karta e Klientit</div>
                <div className="font-display text-2xl text-[#FFD600]">HidroGV</div>
              </div>
              <Gift size={28} className="text-[#FFD600]" />
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <div className="bg-white p-3 rounded flex-shrink-0">
                {qrImgUrl ? (
                  <img src={qrImgUrl} alt="QR Code" data-testid="loyalty-qr" className="w-40 h-40 sm:w-44 sm:h-44" />
                ) : (
                  <div className="w-40 h-40 sm:w-44 sm:h-44 flex items-center justify-center text-neutral-400 text-xs">Duke ngarkuar...</div>
                )}
              </div>
              <div className="flex-1 w-full space-y-2 text-sm">
                <div>
                  <div className="text-[10px] uppercase text-neutral-400 tracking-wider">Emri</div>
                  <div className="font-bold">{user.name}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase text-neutral-400 tracking-wider">Klient Nr.</div>
                  <div className="font-mono text-[#FFD600] text-lg font-bold" data-testid="loyalty-client-nr">{loyalty?.client_number || "—"}</div>
                </div>
                <div className="pt-2 border-t border-[#1F2B44]">
                  <div className="text-[10px] uppercase text-neutral-400 tracking-wider">Blerje totale</div>
                  <div className="font-display text-2xl text-emerald-400" data-testid="loyalty-lifetime">{fmtEUR(loyalty?.lifetime_spend || 0)}</div>
                </div>
              </div>
            </div>
          </div>

          {loyalty?.active_tier && (
            <div className="hg-card p-4 bg-emerald-500/10 border-emerald-500/40" data-testid="active-bonus">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-emerald-500 text-black flex items-center justify-center">
                  <Gift size={24} />
                </div>
                <div className="flex-1">
                  <div className="text-[10px] uppercase text-emerald-400 tracking-widest font-bold">Bonusi aktiv</div>
                  <div className="font-display text-2xl">{loyalty.active_tier.discount_pct}% Zbritje</div>
                  <div className="text-xs text-neutral-400">Përdore në blerjen tënde të ardhshme te arka</div>
                </div>
              </div>
            </div>
          )}

          {loyalty?.next_tier && (
            <div className="hg-card p-4">
              <div className="flex items-center gap-3 mb-3">
                <TrendingUp size={18} className="text-[#FFD600]" />
                <div className="text-xs uppercase tracking-widest text-[#FFD600] font-bold">Bonusi i ardhshëm</div>
              </div>
              <div className="text-sm mb-2">
                Bli edhe <span className="font-bold text-[#FFD600]">{fmtEUR(loyalty.amount_to_next)}</span> për të fituar <span className="font-bold text-emerald-400">{loyalty.next_tier.discount_pct}% zbritje</span>
              </div>
              <div className="w-full bg-[#0B1220] h-3 border border-[#1F2B44] overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#FFD600] to-emerald-400" style={{width: `${Math.min(100, (loyalty.lifetime_spend / loyalty.next_tier.min_spend) * 100)}%`}}></div>
              </div>
              <div className="flex justify-between text-[10px] text-neutral-500 mt-1">
                <span>{fmtEUR(loyalty.lifetime_spend)}</span>
                <span>{fmtEUR(loyalty.next_tier.min_spend)}</span>
              </div>
            </div>
          )}

          <div className="hg-card p-4">
            <div className="text-xs uppercase tracking-widest text-neutral-400 font-bold mb-2">Si funksionon?</div>
            <ol className="text-xs text-neutral-300 space-y-1 list-decimal list-inside">
              <li>Shko në dyqan me telefonin</li>
              <li>Trego QR Code-in te arka</li>
              <li>Arkëtari e skanon dhe të njeh automatikisht</li>
              <li>Bonusi aplikohet automatikisht në blerje</li>
            </ol>
          </div>
        </div>
      )}

      {tab === "orders" && (
        <div className="space-y-4">
          {orders.length === 0 ? (
            <div className="hg-card p-8 text-center" data-testid="orders-empty">
              <ShoppingBag size={40} className="mx-auto text-neutral-600 mb-3" />
              <div className="font-display text-xl mb-1">Nuk keni porosi ende</div>
              <Link to="/kategori" className="hg-btn-primary inline-block mt-3 px-6 py-2.5 text-sm uppercase tracking-wider">Shfleto produktet</Link>
            </div>
          ) : (
            <>
              {active.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#FFD600] font-bold mb-3">Aktive ({active.length})</div>
                  <div className="space-y-3">{active.map(o => <OrderCard key={o.id} o={o} />)}</div>
                </div>
              )}
              {past.length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-widest text-neutral-400 font-bold mb-3 mt-6">Historiku ({past.length})</div>
                  <div className="space-y-3">{past.map(o => <OrderCard key={o.id} o={o} />)}</div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === "invoices" && (
        <div className="space-y-3">
          {loading ? (
            <div className="text-center text-neutral-400 py-16">Duke ngarkuar...</div>
          ) : posInvoices.length === 0 ? (
            <div className="hg-card p-8 text-center">
              <Receipt size={40} className="mx-auto text-neutral-600 mb-3" />
              <div className="font-display text-xl mb-1">Nuk keni fatura nga arka ende</div>
              <div className="text-sm text-neutral-400">Kur skanoni kartën në dyqan, faturat do të shfaqen këtu</div>
            </div>
          ) : (
            posInvoices.map(inv => (
              <div key={inv.id} className="hg-card p-4" data-testid={`inv-${inv.invoice_number}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="text-xs text-neutral-400 uppercase">Faturë</div>
                    <div className="font-mono font-bold">{inv.invoice_number}</div>
                    <div className="text-[10px] text-neutral-500">{new Date(inv.created_at).toLocaleString("sq-AL")}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-display text-xl text-[#FFD600]">{fmtEUR(inv.total)}</div>
                    {inv.bonus_amount > 0 && <div className="text-[10px] text-emerald-400">−{fmtEUR(inv.bonus_amount)} bonus</div>}
                  </div>
                </div>
                <div className="text-xs text-neutral-400 space-y-0.5 pt-2 border-t border-[#1F2B44]">
                  {(inv.items || []).slice(0, 3).map((it, i) => (
                    <div key={i} className="flex justify-between">
                      <span className="truncate">{it.qty}× {it.name}</span>
                      <span className="text-neutral-500 ml-2">{fmtEUR(it.line_total)}</span>
                    </div>
                  ))}
                  {(inv.items?.length || 0) > 3 && <div className="text-neutral-500 italic">+ {inv.items.length - 3} të tjera</div>}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === "profile" && (
        <div className="hg-card p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs uppercase tracking-widest text-[#FFD600] font-bold">Të dhënat e ruajtura</div>
            {editing ? (
              <button onClick={saveProfile} data-testid="profile-save" className="hg-btn-primary px-3 py-1.5 text-xs uppercase inline-flex items-center gap-1"><Save size={12} /> Ruaj</button>
            ) : (
              <button onClick={() => setEditing(true)} data-testid="profile-edit" className="text-xs uppercase tracking-wider px-3 py-1.5 border border-[#1F2B44] hover:border-[#FFD600] hover:text-[#FFD600] inline-flex items-center gap-1"><Edit3 size={12} /> Ndrysho</button>
            )}
          </div>
          <div className="space-y-3">
            {[
              { key: "name", label: "Emri dhe mbiemri", icon: User, type: "text" },
              { key: "company_name", label: "Emri i firmës (opsional)", icon: Building2, type: "text" },
              { key: "phone", label: "Telefoni", icon: Phone, type: "tel" },
              { key: "street", label: "Rruga", icon: MapPin, type: "text" },
              { key: "house_number", label: "Numri i shtëpisë", icon: MapPin, type: "text" },
              { key: "postal_code", label: "Kodi postar", icon: MapPin, type: "text" },
              { key: "city", label: "Qyteti", icon: Building2, type: "text" },
              { key: "country", label: "Shteti", icon: Building2, type: "select", options: enabledCountries },
              { key: "preferred_contact", label: "Preferoni të kontaktoheni me", icon: Phone, type: "select", options: ["Telefon","WhatsApp","Viber","Email"] },
            ].map(f => (
              <div key={f.key}>
                <label className="text-[10px] uppercase tracking-widest text-neutral-400 flex items-center gap-1.5 mb-1"><f.icon size={10} /> {f.label}</label>
                {editing ? (
                  f.type === "select" ? (
                    <select value={form[f.key] || f.options[0]} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                      data-testid={`profile-input-${f.key}`}
                      className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 focus:border-[#FFD600] outline-none text-sm">
                      {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input type={f.type} value={form[f.key] || ""} onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                      data-testid={`profile-input-${f.key}`}
                      className="w-full bg-[#131E30] border border-[#1F2B44] px-3 py-2 focus:border-[#FFD600] outline-none text-sm" />
                  )
                ) : (
                  <div className="px-3 py-2 bg-[#0B1220]/40 border border-[#1F2B44] text-sm" data-testid={`profile-value-${f.key}`}>
                    {user[f.key] || <span className="text-neutral-500 italic">— pa vlerë —</span>}
                  </div>
                )}
              </div>
            ))}
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-400 flex items-center gap-1.5 mb-1"><Mail size={10} /> Email</label>
              <div className="px-3 py-2 bg-[#0B1220]/40 border border-[#1F2B44] text-sm text-neutral-500">{user.email}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
